import { Injectable } from "@angular/core";
import { format, parse, parseISO, isValid, addDays as dateFnsAddDays } from 'date-fns';
import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';
import { LocalStorageConstants } from "../shared/constants/localStorage.constants";
import { LENIENT_TIME_FORMATS } from "../shared/constants/time-formats.constants";
import { UserProfile } from '../models/userProfile.model';
import { DateFnsDateFormat, DateFnsTimeFormat } from "../enums/timePreference.enum";
import { BehaviorSubject } from "rxjs";
import { FormField } from "../shared/form/formField.model";

@Injectable({
    providedIn: 'root'
})
export class DateFormatService {
    readonly SECONDS_THRESHOLD = 1e12;
    readonly shortDateFormat = 'dd MMM yyyy';
    readonly dateFormatHMSA = 'dd MMM, yyyy hh:mm:ss a';
    readonly dateToStringWithTimeFormat = "yyyy-MM-dd HH:mm:ss";
    readonly dateToStringWithTimeFormatNoSeconds = "yyyy-MM-dd HH:mm";
    readonly dateToStringFormat = "yyyy-MM-dd";
    readonly dateToTimeFormat = "HH:mm";
    readonly dateToTimeFormatNoLeadingZero = "H:mm";
    readonly dateToTimeFormat12 = "hh:mm a";
    readonly dateToTimeFormat12NoLeadingZero = "h:mm a";
    readonly dateToTimeFormat12NoLeadingZeroWithSeconds = "h:mm:ss a";
    readonly dateStartingWithMonthFormat = "MMMM dd, yyyy";
    readonly dateStartingWithMonthSmallerFormat = "MMM dd, yy";
    readonly monthDayYearFormat = "MMM d, yyyy";
    readonly ddMmYyyyFormat = "dd/MM/yyyy";
    readonly storeDateTimeFormat = "yyyy-MM-dd'T'HH:mm:ss";
    readonly datetimeLocalFormat = "yyyy-MM-dd'T'HH:mm";
    readonly isoDateTimeFormat = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'";
    readonly canonicalDateFormat = 'yyyy-MM-dd';
    readonly canonicalTimeFormat = 'HH:mm';
    readonly canonicalTimeWithSecondsFormat = 'HH:mm:ss';
    readonly midnightTimeHM = '00:00';
    readonly SECONDS_IN_DAY = 86400;
    private readonly monthDayYearSegmentRegex = /[^A-Za-z]?yyyy[^A-Za-z]?/;
    private readonly minuteSegmentRegex = /mm/;
    private readonly daySegmentRegex = /dd[^A-Za-z]?|[^A-Za-z]dd/;
    private readonly secondSegmentRegex = /ss/;
    private defaultTimeZone: string;
    userDateFormat = DateFnsDateFormat[0];
    userTimeFormat = DateFnsTimeFormat[0];
    userDateWithTimeFormat = `${this.userDateFormat} ${this.userTimeFormat}`;
    userPreferencesSubject = new BehaviorSubject(false);

    setUserDatePreferences(preferences: UserProfile) {
        this.userDateFormat = DateFnsDateFormat[preferences.datePreference] ?? DateFnsDateFormat[0];
        this.userTimeFormat = DateFnsTimeFormat[preferences.timePreference] ?? DateFnsTimeFormat[0];
        this.userDateWithTimeFormat =
            `${this.userDateFormat} ${this.userTimeFormat}`;
        this.userPreferencesSubject.next(true);
    }

    get monthDayFormat(): string {
        return this.userDateFormat.replace(this.monthDayYearSegmentRegex, '');
    }

    get userTimeWithSecondsFormat(): string {
        return this.userTimeFormat.replace(this.minuteSegmentRegex, 'mm:ss');
    }

    get monthYearFormat(): string {
        return this.userDateFormat.replace(this.daySegmentRegex, '');
    }

    get userTimeWithMillisecondsFormat(): string {
        return this.userTimeWithSecondsFormat.replace(this.secondSegmentRegex, 'ss.SSS');
    }

    public setDefaultUserTimezone(timeZone: string) {
        this.defaultTimeZone = timeZone;
    }

    public getTimeZone(): string {
        const user = JSON.parse(localStorage.getItem(LocalStorageConstants.userKey));
        if (user?.userTimeZone) return user.userTimeZone;
        return this.defaultTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    formatTimestampToString(timestamp: number, formatStr: string = this.userDateWithTimeFormat): string {
        return formatInTimeZone(new Date(this.normalizeTimestamp(timestamp)), this.getTimeZone(), formatStr);
    }

    formatDateTimeToString(date: Date, formatStr: string = this.userDateWithTimeFormat): string {
        return format(date, formatStr);
    }

    formatDateTimeWithTimeZoneToString(date: Date, formatStr: string = this.userDateWithTimeFormat): string {
        return formatInTimeZone(date, this.getTimeZone(), formatStr);
    }

    convertStringToDate(dateString: string): Date | undefined {
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? undefined : date;
    }

    convertStringToTimestamp(dateTime: string): number {
        return this.convertDateToTimestamp(this.convertStringToDate(dateTime));
    }

    convertDateToTimestamp(date: Date | undefined): number {
        if (!date || Number.isNaN(date.getTime())) {
            return NaN;
        }
        const formatted = format(date, this.dateToStringWithTimeFormat);
        return fromZonedTime(this.parseDateComponents(formatted), this.getTimeZone()).getTime() / 1000;
    }

    normalizeTimestamp(timestamp: number): number {
        return timestamp < this.SECONDS_THRESHOLD ? timestamp * 1000 : timestamp;
    }

    convertUtcToUserTimeZoneDate(timestamp: number): Date {
        const formatted = formatInTimeZone(new Date(this.normalizeTimestamp(timestamp)), this.getTimeZone(), this.dateToStringWithTimeFormat);
        return new Date(formatted);
    }

    formatTimestampAsUtcString(timestamp: number, formatStr: string = this.userDateWithTimeFormat): string {
        return formatInTimeZone(new Date(this.normalizeTimestamp(timestamp)), 'UTC', formatStr);
    }

    formatUtcTimestampToUserTimezone(timestamp: number, formatStr: string = this.userDateWithTimeFormat): string {
        return formatInTimeZone(new Date(this.normalizeTimestamp(timestamp)), this.getTimeZone(), formatStr);
    }

    convertTimestampToDate(timestamp: number): Date {
        const formatted = formatInTimeZone(new Date(this.normalizeTimestamp(timestamp)), this.getTimeZone(), this.dateToStringWithTimeFormat);
        return new Date(formatted);
    }

    convertUserTimeZoneStringToUTCDateString(userTzString: string, formatStr = this.storeDateTimeFormat): string {
        const parsed = this.parseDateComponents(userTzString);
        const utcDate = fromZonedTime(parsed, this.getTimeZone());
        return formatInTimeZone(utcDate, 'UTC', formatStr);
    }

    convertUTCDateStringToUserTimeZoneString(dateString: string, formatStr = this.storeDateTimeFormat): string {
        const parsed = this.parseDateComponents(dateString);
        const utcDate = new Date(Date.UTC(
            parsed.getFullYear(), parsed.getMonth(), parsed.getDate(),
            parsed.getHours(), parsed.getMinutes(), parsed.getSeconds()
        ));
        return formatInTimeZone(utcDate, this.getTimeZone(), formatStr);
    }

    getStartOfDay(date: Date): Date {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    }

    getEndOfDay(date: Date): Date {
        const d = new Date(date);
        d.setHours(23, 59, 59, 999);
        return d;
    }

    formatDateToDdMmYyyy(date: Date | string | number): string {
        if (!date) return '';

        let dateObj: Date;

        if (typeof date === 'string') {
            dateObj = new Date(date);
        } else if (typeof date === 'number') {
            dateObj = this.convertTimestampToDate(date);
        } else {
            dateObj = date;
        }

        if (isNaN(dateObj.getTime())) {
            return '';
        }

        return format(dateObj, this.ddMmYyyyFormat);
    }

    private get dateParseFormats(): string[] {
        const list = [this.userDateFormat, this.canonicalDateFormat, this.ddMmYyyyFormat];
        return Array.from(new Set(list.filter(Boolean)));
    }

    private get timeParseFormats(): string[] {
        const list = [
            // 12-hour AM/PM formats must come first to prevent H:mm from silently ignoring " PM" suffix
            this.dateToTimeFormat12NoLeadingZero,           // h:mm a — unpadded 12h with AM/PM
            this.dateToTimeFormat12NoLeadingZeroWithSeconds, // h:mm:ss a — unpadded 12h with seconds and AM/PM
            this.userTimeFormat,                              // depends on user preference: hh:mm a or HH:mm
            this.dateToTimeFormat12,                          // hh:mm a — padded 12h with AM/PM
            // 24-hour formats must come after 12h formats
            this.canonicalTimeFormat,                         // HH:mm — canonical 24h padded
            this.canonicalTimeWithSecondsFormat,              // HH:mm:ss — canonical 24h padded with seconds
            this.dateToTimeFormatNoLeadingZero,               // H:mm — unpadded 24h
        ];
        return Array.from(new Set(list.filter(Boolean)));
    }

    parseUserDateToYMD(value: string): string | null {
        if (!value) return null;
        const trimmed = value.trim();

        for (const fmt of this.dateParseFormats) {
            const parsed = parse(trimmed, fmt, new Date());
            if (isValid(parsed) && format(parsed, fmt) === trimmed) {
                return format(parsed, this.canonicalDateFormat);
            }
        }

        const iso = parseISO(trimmed);
        if (isValid(iso)) {
            return format(iso, this.canonicalDateFormat);
        }

        return null;
    }

    parseUserTimeToHM(value: string): string | null {
        if (!value) return null;
        const trimmed = value.trim();

        for (const fmt of this.timeParseFormats) {
            const parsed = parse(trimmed, fmt, new Date());
            if (isValid(parsed) && format(parsed, fmt) === trimmed) {
                return format(parsed, this.canonicalTimeFormat);
            }
        }

        // Fallback: try lenient parsing with single-digit hour format — users often omit leading zeros in time input
        for (const fmt of LENIENT_TIME_FORMATS) {
                const parsed = parse(trimmed, fmt, new Date());
                if (isValid(parsed)) {
                    return format(parsed, this.canonicalTimeFormat);
                }
        }

        return null;
    }

    utcToLocalDateAndTime(utc: string): { date: string; time: string } {
        const local = this.convertUTCDateStringToUserTimeZoneString(utc);
        const parsed = this.parseDateComponents(local);
        return { date: format(parsed, this.canonicalDateFormat), time: format(parsed, this.canonicalTimeFormat) };
    }

    localDateTimeToUTC(datePart: string, timeHM: string): string {
        const full = `${datePart}T${timeHM}:00`;
        return this.convertUserTimeZoneStringToUTCDateString(full);
    }

    localDateTimeString(datePart: string, timeHM: string): string {
        return `${datePart}T${timeHM}:00`;
    }

    userTimezoneToLocalDateAndTime(date: number): { date: string; time: string } {
        const dateString = this.formatTimestampToString(date, this.storeDateTimeFormat);
        const parsed = this.parseDateComponents(dateString);
        return { date: format(parsed, this.canonicalDateFormat), time: format(parsed, this.canonicalTimeFormat) };
    }

    addDays(datePart: string, days: number = 1): string {
        const parsed = parse(datePart, this.canonicalDateFormat, new Date());
        return format(dateFnsAddDays(parsed, days), this.canonicalDateFormat);
    }

    /**
     * Parse a datetime string like "2023-02-02T07:00:00" into a Date with those
     * exact time components in the machine's local timezone.
     * This avoids timezone interpretation issues with new Date() for ISO-like strings.
     */
    public parseDateComponents(dateStr: string): Date {
        const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}):(\d{2}))?/);
        if (!match) return new Date(dateStr);
        const [, y, mo, d, h = '0', mi = '0', s = '0'] = match;
        return new Date(+y, +mo - 1, +d, +h, +mi, +s);
    }
    
    applyTimeChange<T>(entity: T, field: FormField, event: string, fieldItem?: FormField, index = -1): void {
        const time24 = this.parseUserTimeToHM(event) ?? event;
        if (fieldItem) {
            const items = entity[field.property];
            if (!Array.isArray(items) || index < 0 || index >= items.length) return;
            items[index][fieldItem.property] = time24;
            fieldItem.onChange?.(time24, field, index);
        } else {
            entity[field.property] = time24;
            field.onChange?.(time24, field);
        }
    }
}
